import { Link, NavLink } from "react-router-dom";
import { useState } from "react";
import BrandLogo from "./BrandLogo";
import { Button } from "./ui/button";
import { Menu, X, ShieldCheck, Globe2 } from "lucide-react";

const NAV = [
  { to: "/solutions", label: "Solutions" },
  { to: "/pricing", label: "Pricing" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
  { to: "/verify", label: "Verify Certificate" },
];

export default function PublicShell({ children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="sticky top-0 z-40 glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <BrandLogo />
          <nav className="hidden lg:flex items-center gap-7 text-sm font-medium text-slate-700">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `transition-colors ${isActive ? "text-[#0F4C81]" : "hover:text-[#0F4C81]"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden md:flex items-center gap-3">
            <Link to="/login" data-testid="public-signin-link">
              <Button variant="ghost" className="text-[#0A192F] hover:text-[#0F4C81]">
                Sign in
              </Button>
            </Link>
            <Link to="/demo" data-testid="public-demo-link">
              <Button className="bg-[#D4AF37] text-[#0A192F] hover:bg-[#B5952F] font-bold">
                Book demo
              </Button>
            </Link>
          </div>
          <button
            data-testid="public-menu-toggle"
            className="lg:hidden h-9 w-9 rounded-md flex items-center justify-center text-[#0A192F] hover:bg-slate-100"
            onClick={() => setOpen((v) => !v)}
            aria-label="Open menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
        {open && (
          <div className="lg:hidden border-t border-slate-200 bg-white">
            <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-3">
              {NAV.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  className="text-sm font-medium text-slate-700 hover:text-[#0F4C81]"
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {n.label}
                </Link>
              ))}
              <div className="flex items-center gap-3 pt-2">
                <Link to="/login" className="flex-1">
                  <Button variant="outline" className="w-full">Sign in</Button>
                </Link>
                <Link to="/demo" className="flex-1">
                  <Button className="w-full bg-[#D4AF37] text-[#0A192F] hover:bg-[#B5952F] font-bold">Book demo</Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <PublicFooter />
    </div>
  );
}

export function PublicFooter() {
  return (
    <footer className="bg-[#0A192F] border-t border-white/10 text-slate-400">
      <div className="max-w-7xl mx-auto px-6 py-14 grid grid-cols-2 md:grid-cols-5 gap-8 md:gap-10">
        <div className="col-span-2">
          <BrandLogo light />
          <p className="mt-4 text-sm max-w-sm leading-relaxed">
            Diplomexa is the operating system for global Model UN, debate, youth
            parliament, SDG and student leadership conferences — used by
            secretariats who refuse to settle.
          </p>
          <div className="mt-6 flex items-center gap-4 text-xs">
            <span className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-[#D4AF37]" /> SOC-2 ready</span>
            <span className="flex items-center gap-1.5"><Globe2 className="h-3.5 w-3.5 text-[#D4AF37]" /> Hosted globally</span>
          </div>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">Product</p>
          <ul className="mt-4 space-y-2 text-sm">
            <li><Link to="/solutions" className="hover:text-white">Solutions</Link></li>
            <li><Link to="/pricing" className="hover:text-white">Pricing</Link></li>
            <li><Link to="/verify" className="hover:text-white">Verify certificate</Link></li>
            <li><Link to="/demo" className="hover:text-white">Book a demo</Link></li>
          </ul>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">Company</p>
          <ul className="mt-4 space-y-2 text-sm">
            <li><Link to="/about" className="hover:text-white">About</Link></li>
            <li><Link to="/contact" className="hover:text-white">Contact &amp; Grievance</Link></li>
            <li><a href="mailto:info@factskoot.com" className="hover:text-white">info@factskoot.com</a></li>
            <li><a href="mailto:info@destineze.com" className="hover:text-white">info@destineze.com</a></li>
            <li><a href="tel:+919844139333" className="hover:text-white">+91 98441 39333</a></li>
            <li><a href="tel:+971504883656" className="hover:text-white">+971 50 488 3656</a></li>
            <li><a href="https://www.factskoot.com" target="_blank" rel="noreferrer" className="hover:text-white">www.factskoot.com</a></li>
            <li><a href="https://www.destineze.com" target="_blank" rel="noreferrer" className="hover:text-white">www.destineze.com</a></li>
          </ul>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">Legal</p>
          <ul className="mt-4 space-y-2 text-sm">
            <li><Link to="/terms" className="hover:text-white">Terms &amp; Conditions</Link></li>
            <li><Link to="/privacy" className="hover:text-white">Privacy Policy</Link></li>
            <li><Link to="/refund-policy" className="hover:text-white">Refund &amp; Cancellation</Link></li>
            <li><Link to="/cancellation-policy" className="hover:text-white">Cancellation</Link></li>
            <li><Link to="/cookie-policy" className="hover:text-white">Cookie Policy</Link></li>
            <li><Link to="/billing-notice" className="hover:text-white">Billing Entity Notice</Link></li>
            <li><Link to="/shipping-policy" className="hover:text-white">Digital Delivery</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-wrap items-center justify-between text-xs text-slate-500 gap-3">
          <p data-testid="footer-attribution">
            © 2026 Diplomexa · <span className="text-slate-400">Powered by Diplomexa by{" "}
              <a href="https://www.destineze.com" target="_blank" rel="noreferrer" className="hover:text-white underline-offset-2 hover:underline">Destineze</a>{" "}
              and{" "}
              <a href="https://www.factskoot.com" target="_blank" rel="noreferrer" className="hover:text-white underline-offset-2 hover:underline">Factskoot</a>
            </span>
          </p>
          <p className="font-mono">v3.0 · From conferences to global citizens</p>
        </div>
      </div>
    </footer>
  );
}
